import React from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useAuth } from "@/contexts/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { UserMenu } from "@/components/UserMenu";
import ravenLogo from "@/assets/raven-logo.png";
import { getImageUrl } from "@/lib/imageUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";

interface AuthenticatedLayoutProps {
  children: React.ReactNode;
}

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const { empresa, hasRole } = useAuth();
  const isMobile = useIsMobile();
  
  // Mobile layout with Sheet/Drawer
  if (isMobile) {
    return (
      <MobileLayout empresa={empresa} hasRole={hasRole}>
        {children}
      </MobileLayout>
    );
  }
  
  // Desktop/Tablet layout with Sidebar
  return (
    <SidebarProvider defaultOpen={true}>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 md:h-16 border-b border-border/50 bg-card/80 backdrop-blur-lg supports-[backdrop-filter]:bg-card/60 sticky top-0 z-40">
            <div className="flex items-center justify-between h-full px-3 md:px-4">
              <div className="flex items-center gap-2 md:gap-4">
                <SidebarTrigger className="mr-1 md:mr-2" />
                {empresa?.logo_url && !hasRole('super_admin') ? (
                  <div className="relative h-7 w-7 md:h-8 md:w-8 rounded-md border border-border/50 overflow-hidden bg-card flex items-center justify-center">
                    <img 
                      src={getImageUrl(empresa.logo_url) || ""} 
                      alt={empresa.nombre} 
                      className="h-5 w-5 md:h-6 md:w-6 object-contain"
                    />
                  </div>
                ) : (
                  <img src={ravenLogo} alt="Raven" className="h-7 w-7 md:h-8 md:w-8 object-contain" />
                )}
                <h1 className="text-base md:text-lg font-display uppercase tracking-wider truncate max-w-[150px] md:max-w-none">
                  {empresa?.nombre && !hasRole('super_admin') ? empresa.nombre : 'Raven'}
                </h1>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <UserMenu />
                <ThemeToggle />
              </div>
            </div>
          </header>
          <main className="flex-1 p-3 md:p-4 lg:p-6">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

// Mobile Layout Component with Sheet/Drawer
interface MobileLayoutProps {
  children: React.ReactNode;
  empresa: any;
  hasRole: (role: string) => boolean;
}

function MobileLayout({ children, empresa, hasRole }: MobileLayoutProps) {
  const [open, setOpen] = React.useState(false);
  
  return (
    <div className="min-h-screen flex flex-col w-full bg-background">
      <header className="h-14 border-b border-border/50 bg-card/80 backdrop-blur-lg supports-[backdrop-filter]:bg-card/60 sticky top-0 z-40">
        <div className="flex items-center justify-between h-full px-3">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11 -ml-2" aria-label="Abrir menú">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 overflow-y-auto">
                <MobileSidebarContent onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            {empresa?.logo_url && !hasRole('super_admin') ? (
              <div className="relative h-7 w-7 rounded-md border border-border/50 overflow-hidden bg-card flex items-center justify-center">
                <img 
                  src={getImageUrl(empresa.logo_url) || ""} 
                  alt={empresa.nombre} 
                  className="h-5 w-5 object-contain"
                />
              </div>
            ) : (
              <img src={ravenLogo} alt="Raven" className="h-7 w-7 object-contain" />
            )}
            <h1 className="text-base font-display uppercase tracking-wider truncate max-w-[140px]">
              {empresa?.nombre && !hasRole('super_admin') ? empresa.nombre : 'Raven'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <UserMenu />
            <ThemeToggle />
          </div>
        </div>
      </header>
      <main className="flex-1 p-3">
        {children}
      </main>
    </div>
  );
}

// Mobile Sidebar Content (inside Sheet)
import { NavLink, useLocation } from "react-router-dom";
import { 
  Home, Users, Shield, FileText, FileSignature, UserCog, LogOut,
  FileCheck, Settings, Building2, ClipboardList, Calendar,
  AlertTriangle, Code, LayoutGrid, CalendarClock, Inbox, Package, Plane, Wrench, Radio
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface MobileSidebarContentProps {
  onNavigate: () => void;
}

const getIconColor = (title: string) => {
  const colorMap: Record<string, string> = {
    "Dashboard": "text-[hsl(var(--raven-ice))]",
    "Gestión de Empresas": "text-[hsl(var(--raven-violet))]",
    "Ver Clientes": "text-[hsl(var(--raven-violet))]",
    "Clientes con Abonado": "text-red-400",
    "Partes de Trabajo": "text-cyan-400",
    "Seguimiento de Obras": "text-orange-400",
    "Mandato SEPA": "text-blue-400",
    "Mi Calendario": "text-cyan-400",
    "Gestión de OTs": "text-purple-400",
  };
  return colorMap[title] || "text-[hsl(var(--raven-ice))]";
};

const getAdminIconColor = (title: string) => {
  const colorMap: Record<string, string> = {
    "Jornadas Equipo": "text-orange-400",
    "Gestión Usuarios": "text-[hsl(var(--raven-violet))]",
    "Códigos de Anomalías": "text-yellow-400",
    "Herramientas": "text-green-400",
  };
  return colorMap[title] || "text-purple-400";
};

const getMainItems = (userRole: string) => {
  if (userRole === "super_admin") {
    return [
      { title: "Gestión de Empresas", url: "/super-admin/empresas", icon: Building2 },
      { title: "Buzón de Feedback", url: "/admin/feedback", icon: Inbox },
    ];
  }

  const commonItems = [
    { title: "Dashboard", url: "/", icon: Home },
    { title: "Ver Clientes", url: "/clientes", icon: Users },
    { title: "Clientes con Abonado", url: "/clientes-abonados", icon: Radio },
    { title: "Partes de Trabajo", url: "/partes-trabajo", icon: FileText },
    { title: "Recepción Material", url: "/recepciones", icon: Package },
  ];

  const tecnicoItems = [
    { title: "Mi Calendario", url: "/mi-calendario", icon: Calendar },
    { title: "Mis Ausencias", url: "/mis-ausencias", icon: Plane },
  ];
  const gestorItems = [
    { title: "Gestión de OTs", url: "/asignacion-tareas", icon: LayoutGrid },
    { title: "Mantenimientos", url: "/mantenimientos", icon: Wrench },
    { title: "Jornadas Equipo", url: "/jornadas-equipo", icon: CalendarClock },
    { title: "Ausencias Equipo", url: "/ausencias-equipo", icon: Plane },
  ];
  const obrasItems = [
    { title: "Seguimiento de Obras", url: "/obras", icon: Building2 },
  ];
  const adminOnlyItems = [
    { title: "Mandato SEPA", url: "/mandato-sepa", icon: FileSignature },
  ];

  switch (userRole) {
    case 'admin': return [...commonItems, ...tecnicoItems, ...gestorItems, ...obrasItems, ...adminOnlyItems];
    case 'gestor': return [...commonItems, ...gestorItems, ...obrasItems];
    case 'tecnico': return [...commonItems, ...tecnicoItems];
    default: return commonItems;
  }
};

const adminItems = [
  { title: "Gestión Usuarios", url: "/gestion-usuarios", icon: UserCog },
  { title: "Códigos de Anomalías", url: "/codigos-anomalias", icon: Code },
  { title: "Herramientas", url: "/herramientas", icon: Wrench },
];

function MobileSidebarContent({ onNavigate }: MobileSidebarContentProps) {
  const location = useLocation();
  const { perfil, signOut, empresa } = useAuth();
  const [showLogoutDialog, setShowLogoutDialog] = React.useState(false);
  
  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await signOut();
  };

  const userRole = perfil?.rol || "";
  const showAdminSection = userRole === "admin";

  return (
    <div className="flex flex-col h-full bg-card">
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="sidebar-logo-glow">
            <img src={ravenLogo} alt="Raven" className="h-10 w-10 object-contain" />
          </div>
          {empresa?.logo_url && perfil?.rol !== 'super_admin' && (
            <div className="relative h-10 w-10 rounded-md border border-border/50 overflow-hidden bg-card flex items-center justify-center">
              <img 
                src={getImageUrl(empresa.logo_url) || ""} 
                alt={empresa.nombre} 
                className="h-8 w-8 object-contain"
              />
            </div>
          )}
          <div>
            <h2 className="sidebar-title-gradient text-xl font-display uppercase tracking-wider">Raven</h2>
            {empresa && perfil?.rol !== 'super_admin' && (
              <Badge variant="outline" className="mt-1 text-xs border-[hsl(var(--raven-ice))] text-[hsl(var(--raven-ice))]">
                {empresa.nombre}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4">
        <div className="px-3">
          <p className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Navegación Principal
          </p>
          <nav className="space-y-1">
            {getMainItems(userRole).map((item) => {
              const active = isActive(item.url);
              return (
                <NavLink
                  key={item.title}
                  to={item.url}
                  end={item.url === "/"}
                  onClick={onNavigate}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active 
                      ? 'bg-primary/10 text-primary' 
                      : 'text-foreground/70 hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <item.icon className={`h-5 w-5 ${getIconColor(item.title)}`} />
                  <span>{item.title}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>

        {showAdminSection && (
          <div className="px-3 mt-6">
            <div className="h-px bg-border/50 mb-4" />
            <p className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Administración
            </p>
            <nav className="space-y-1">
              {adminItems.map((item) => {
                const active = isActive(item.url);
                return (
                  <NavLink
                    key={item.title}
                    to={item.url}
                    onClick={onNavigate}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      active 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-foreground/70 hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <item.icon className={`h-5 w-5 ${getAdminIconColor(item.title)}`} />
                    <span>{item.title}</span>
                  </NavLink>
                );
              })}
            </nav>
          </div>
        )}
      </div>

      {/* Footer - Logout */}
      <div className="p-3 border-t border-border/50">
        <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
          <AlertDialogTrigger asChild>
            <button className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-foreground/70 hover:bg-destructive/10 hover:text-destructive transition-colors">
              <LogOut className="h-5 w-5 text-red-400" />
              <span>Cerrar Sesión</span>
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Cerrar sesión?</AlertDialogTitle>
              <AlertDialogDescription>
                ¿Está seguro que desea cerrar la sesión? Será redirigido a la página de inicio de sesión.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleLogout}>
                Cerrar Sesión
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
